import type {
  ClarifyingAnswerMap,
  ClarifyingAnswerPayload,
  ClarifyingOption,
  ClarifyingQuestion,
} from '@/types'

function isOption(value: unknown): value is ClarifyingOption {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ClarifyingOption).label === 'string'
  )
}

function isQuestion(value: unknown): value is ClarifyingQuestion {
  if (typeof value !== 'object' || value === null) return false
  const q = value as ClarifyingQuestion
  return (
    typeof q.id === 'string' &&
    typeof q.question === 'string' &&
    Array.isArray(q.options) &&
    q.options.every(isOption)
  )
}

/** Pull the fully-formed questions out of the (possibly streaming) tool args. */
export function parseQuestions(
  args: Record<string, unknown> | undefined
): ClarifyingQuestion[] {
  const raw = args?.questions
  if (!Array.isArray(raw)) return []
  return raw.filter(isQuestion)
}

/** Parse a persisted tool result (`{answers, reply}`) back into a payload. */
export function parseAnswerResult(
  result: string | undefined
): ClarifyingAnswerPayload | null {
  if (!result) return null
  try {
    const parsed = JSON.parse(result) as Partial<ClarifyingAnswerPayload>
    if (parsed && typeof parsed === 'object' && 'answers' in parsed) {
      return {
        answers: (parsed.answers ?? {}) as ClarifyingAnswerMap,
        reply: typeof parsed.reply === 'string' ? parsed.reply : undefined,
      }
    }
  } catch {
    // Not our JSON shape — ignore and fall through.
  }
  return null
}

/** A draft per-question answer accumulated in local component state. */
export interface QuestionDraft {
  selected: string[]
  freeText: string
  skipped: boolean
}

export function emptyDraft(): QuestionDraft {
  return { selected: [], freeText: '', skipped: false }
}

/** A question is resolved once it has a choice, free text, or is skipped. */
export function isResolved(draft: QuestionDraft): boolean {
  return (
    draft.skipped ||
    draft.selected.length > 0 ||
    draft.freeText.trim().length > 0
  )
}

/** Collapse drafts into the answer map submitted as the tool result. */
export function draftsToAnswers(
  questions: ClarifyingQuestion[],
  drafts: Record<string, QuestionDraft>
): ClarifyingAnswerMap {
  const answers: ClarifyingAnswerMap = {}
  for (const q of questions) {
    const draft = drafts[q.id] ?? emptyDraft()
    answers[q.id] = {
      selected: draft.selected,
      free_text: draft.freeText.trim() || undefined,
      skipped: draft.skipped || undefined,
    }
  }
  return answers
}
